import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, CircularProgress, Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';

// Referencia ao IP do host ao invés de localhost ou nome do serviço
const API_URL = process.env.REACT_APP_API_URL || 'https://45.225.80.187:8010';
console.log("Using API URL:", API_URL);

let socket;

const RTSPTester = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testId, setTestId] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  
  // Connect to Socket.IO when component mounts
  useEffect(() => {
    // Usar a mesma URL base da API, mas removendo /api se presente para WebSocket
    let socketUrl = API_URL.replace('/api', '');
    
    console.log('Conectando ao Socket.IO em:', socketUrl);
    
    socket = io(socketUrl, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 20000,
      autoConnect: true,
      withCredentials: false,
    });
    
    socket.on('connect', () => {
      console.log('Connected to WebSocket', socket.id);
      setSocketConnected(true);
      // Enviar ping para testar conexão
      socket.emit('ping', { message: 'Hello from client' }, (response) => {
        console.log('Ping response:', response);
      });
    });
    
    socket.on('connect_confirmed', (data) => {
      console.log('Server confirmed connection:', data);
    });
    
    socket.on('disconnect', () => {
      console.log('Disconnected from WebSocket');
      setSocketConnected(false);
    });
    
    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      console.error('Socket connection error details:', error.message);
    });
    
    socket.on('test_update', (data) => {
      console.log('Received test_update event:', data);
      if (data.test_id === testId) {
        console.log(`Update matches current test ID: ${testId}`, data);
        
        // Atualize o status do teste independentemente do tipo de atualização
        if (data.status) {
          console.log(`Test status changed to: ${data.status}`);
          
          if (data.status === 'processing') {
            // Redirect to the test page as soon as we receive the processing status
            navigate(`/test/${testId}`);
          }
        }
      } else {
        console.log(`Update for different test ID: ${data.test_id}, current: ${testId}`);
      }
    });
    
    return () => {
      if (socket) socket.disconnect();
    };
  }, [testId, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset previous state
    setError('');
    setTestId(null);
    
    // Validate URL
    if (!url.startsWith('rtsp://') && !url.startsWith('rtsps://')) {
      setError('URL must start with rtsp:// or rtsps://');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('Submitting URL:', url);
      // Enviar dados no formato { url: 'rtsp://...' }
      const response = await axios.post(`${API_URL}/rtsp/test`, { url });
      console.log('Response:', response.data);
      
      // Set the test ID and immediately redirect to the test page
      const newTestId = response.data.id;
      setTestId(newTestId);
      
      // Redirect to the test page
      navigate(`/test/${newTestId}`);
      
    } catch (err) {
      setLoading(false);
      console.error('Request error:', err);
      
      // Tratamento detalhado do erro
      if (err.response?.data) {
        console.log('Error response data:', err.response.data);
        
        // Formato de erro do FastAPI
        if (err.response.data.detail) {
          const detail = err.response.data.detail;
          
          // Array de erros de validação
          if (Array.isArray(detail)) {
            // Pegar apenas o primeiro erro para simplicidade
            const firstError = detail[0];
            if (firstError.msg) {
              setError(firstError.msg);
            } else {
              setError(JSON.stringify(firstError));
            }
          } 
          // Objeto de erro único
          else if (typeof detail === 'object' && detail !== null) {
            if (detail.msg) {
              setError(detail.msg);
            } else {
              setError(JSON.stringify(detail));
            }
          } 
          // String simples
          else {
            setError(detail);
          }
        } else {
          setError(JSON.stringify(err.response.data));
        }
      } else {
        setError('An error occurred while submitting the test');
      }
    }
  };
  
  return (
    <Box sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {!socketConnected && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            WebSocket connection not established. Real-time updates may not work.
          </Alert>
        )}
      
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                label="RTSP URL"
                variant="outlined"
                placeholder="rtsp://example.com:554/stream"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                disabled={loading}
                helperText="Enter a valid RTSP URL, e.g. rtsp://username:password@camera-ip:554/stream"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Test Stream'}
              </Button>
            </Grid>
          </Grid>
        </form>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default RTSPTester;